/**
 * /settings/insurance — tenant partner directory + per-tenant overrides.
 *
 * Lists every partner the tenant sees (platform-wide library + tenant-added),
 * lets the tenant toggle the whole directory, add a custom partner, override
 * a platform partner's referral URL or disable it.
 *
 * Capability: insurance.configure.
 */
import { useConfirm } from '@/components/Confirm';
import { useCallback, useEffect, useState } from 'react';

interface Partner {
  id: string;
  name: string;
  countryCode: string;
  description: string;
  applicationUrl: string;
  referralUrl: string | null;
  logoUrl: string | null;
  supportedSpecies: string[];
  isPlatform: boolean;
  isActive: boolean;
  position: number;
  override: { disabled?: boolean; referralUrl?: string | null } | null;
}

interface DirectoryResponse {
  enabled: boolean;
  partners: Partner[];
}

const COUNTRY_OPTIONS = [
  { code: 'GB', label: 'United Kingdom' },
  { code: 'IE', label: 'Ireland' },
  { code: 'US', label: 'United States' },
  { code: 'CA', label: 'Canada' },
  { code: 'AU', label: 'Australia' },
  { code: 'NZ', label: 'New Zealand' },
];

export function InsuranceSettings() {
  const [data, setData] = useState<DirectoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingEnabled, setSavingEnabled] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const { confirm, ConfirmDialog } = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/insurance/partners', { credentials: 'include' });
      if (!res.ok) throw new Error(`load failed: ${res.status}`);
      setData((await res.json()) as DirectoryResponse);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleEnabled = useCallback(
    async (next: boolean) => {
      setSavingEnabled(true);
      setSaveError(null);
      try {
        const res = await fetch('/api/insurance/settings', {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ enabled: next }),
        });
        if (!res.ok) {
          setSaveError(`Could not ${next ? 'enable' : 'disable'} the directory. Try again.`);
          return;
        }
        await load();
      } finally {
        setSavingEnabled(false);
      }
    },
    [load],
  );

  const togglePlatformDisabled = useCallback(
    async (partner: Partner, disabled: boolean) => {
      setSaveError(null);
      const res = await fetch(`/api/insurance/platform-overrides/${partner.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ disabled }),
      });
      if (!res.ok) {
        setSaveError(`Could not update "${partner.name}". Try again.`);
        return;
      }
      await load();
    },
    [load],
  );

  const setReferralOverride = useCallback(
    async (partner: Partner, url: string) => {
      setSaveError(null);
      const res = await fetch(`/api/insurance/platform-overrides/${partner.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ referralUrl: url || null }),
      });
      if (!res.ok) {
        setSaveError(`Could not save the referral URL for "${partner.name}". Try again.`);
        return;
      }
      await load();
    },
    [load],
  );

  const deletePartner = useCallback(
    async (id: string) => {
      const yes = await confirm({
        title: 'Delete this custom partner?',
        message: 'Customers will no longer see this partner in the directory.',
        confirmLabel: 'Delete',
        danger: true,
      });
      if (!yes) return;
      setSaveError(null);
      const res = await fetch(`/api/insurance/partners/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        setSaveError('Could not delete the partner. Try again.');
        return;
      }
      await load();
    },
    [confirm, load],
  );

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading insurance partners...</p>;
  }
  if (error || !data) {
    return <p className="text-sm text-destructive">{error ?? 'Failed to load'}</p>;
  }

  return (
    <div className="space-y-6">
      <ConfirmDialog />
      {saveError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {saveError}
        </div>
      )}
      <div>
        <h1 className="text-2xl font-semibold">Insurance partners</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Recommend pet insurance providers to your customers. Customers see the directory at{' '}
          <span className="font-mono">/account/insurance</span> filtered by their country.
        </p>
      </div>

      <div className="rounded-lg border p-4">
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={data.enabled}
            disabled={savingEnabled}
            onChange={(e) => void toggleEnabled(e.currentTarget.checked)}
            className="size-4"
          />
          <span className="font-medium">Insurance directory enabled</span>
        </label>
        <p className="mt-1 text-xs text-muted-foreground">
          When off, the /account/insurance page is hidden from customers.
        </p>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">
          Partners (<span className="num">{data.partners.length}</span>)
        </h2>
        <button
          type="button"
          className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
          onClick={() => setShowAdd(true)}
        >
          Add custom partner
        </button>
      </div>

      {showAdd && (
        <AddPartnerForm
          onSaved={async () => {
            setShowAdd(false);
            await load();
          }}
          onCancel={() => setShowAdd(false)}
        />
      )}

      <div className="overflow-x-auto">
        <table className="min-w-[640px] w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="py-2">Partner</th>
              <th className="py-2">Country</th>
              <th className="py-2">Source</th>
              <th className="py-2">Referral URL</th>
              <th className="py-2">Active</th>
              <th className="py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.partners.map((p) => (
              <tr key={p.id} className="border-b">
                <td className="py-2">
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-muted-foreground">{p.description}</div>
                </td>
                <td className="py-2">{p.countryCode}</td>
                <td className="py-2">
                  {p.isPlatform ? (
                    <span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary">
                      Platform
                    </span>
                  ) : (
                    <span className="rounded bg-success/10 px-1.5 py-0.5 text-xs text-success">
                      Custom
                    </span>
                  )}
                </td>
                <td className="py-2">
                  {p.isPlatform ? (
                    <input
                      type="url"
                      placeholder={p.applicationUrl}
                      defaultValue={p.override?.referralUrl ?? ''}
                      onBlur={(e) => void setReferralOverride(p, e.currentTarget.value)}
                      className="w-full max-w-xs rounded border px-2 py-1 text-xs"
                    />
                  ) : (
                    <a
                      href={p.referralUrl ?? p.applicationUrl}
                      className="text-xs text-primary underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {p.referralUrl ?? p.applicationUrl}
                    </a>
                  )}
                </td>
                <td className="py-2">
                  {p.isPlatform ? (
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!(p.override?.disabled ?? false)}
                        onChange={(e) => void togglePlatformDisabled(p, !e.currentTarget.checked)}
                      />
                      <span className="text-xs">{p.override?.disabled ? 'Hidden' : 'Shown'}</span>
                    </label>
                  ) : p.isActive ? (
                    <span className="text-xs text-success">Active</span>
                  ) : (
                    <span className="text-xs text-muted-foreground">Hidden</span>
                  )}
                </td>
                <td className="py-2 text-right">
                  {!p.isPlatform && (
                    <button
                      type="button"
                      onClick={() => void deletePartner(p.id)}
                      className="text-xs text-destructive underline"
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface AddPartnerFormProps {
  onSaved: () => Promise<void>;
  onCancel: () => void;
}

function AddPartnerForm({ onSaved, onCancel }: AddPartnerFormProps) {
  const [name, setName] = useState('');
  const [countryCode, setCountryCode] = useState('GB');
  const [description, setDescription] = useState('');
  const [applicationUrl, setApplicationUrl] = useState('');
  const [referralUrl, setReferralUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/insurance/partners', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name,
          countryCode,
          description,
          applicationUrl,
          referralUrl: referralUrl || null,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { issues?: string[] } | null;
        throw new Error(body?.issues?.join(', ') ?? `create failed: ${res.status}`);
      }
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [name, countryCode, description, applicationUrl, referralUrl, onSaved]);

  return (
    <form
      className="rounded-lg border p-4 bg-muted/30 space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <h3 className="font-medium">Add custom insurance partner</h3>
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-sm">
          Name
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            className="mt-1 w-full rounded border px-2 py-1.5"
          />
        </label>
        <label className="block text-sm">
          Country
          <select
            value={countryCode}
            onChange={(e) => setCountryCode(e.currentTarget.value)}
            className="mt-1 w-full rounded border px-2 py-1.5"
          >
            {COUNTRY_OPTIONS.map((o) => (
              <option key={o.code} value={o.code}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="block text-sm">
        Description
        <textarea
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          rows={2}
          className="mt-1 w-full rounded border px-2 py-1.5"
        />
      </label>
      <label className="block text-sm">
        Application URL (public)
        <input
          type="url"
          value={applicationUrl}
          onChange={(e) => setApplicationUrl(e.currentTarget.value)}
          className="mt-1 w-full rounded border px-2 py-1.5"
          placeholder="https://insurer.example/quote"
        />
      </label>
      <label className="block text-sm">
        Affiliate / referral URL (optional)
        <input
          type="url"
          value={referralUrl}
          onChange={(e) => setReferralUrl(e.currentTarget.value)}
          className="mt-1 w-full rounded border px-2 py-1.5"
        />
      </label>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy}
          className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {busy ? 'Saving...' : 'Save partner'}
        </button>
        <button type="button" onClick={onCancel} className="rounded border px-3 py-1.5 text-sm">
          Cancel
        </button>
      </div>
    </form>
  );
}
